import { useQuery } from "@tanstack/react-query";
import { formatCurrency, formatNumber } from "../lib/utils";
import { TrendingUp, Calendar, CreditCard } from "lucide-react";

interface ForecastItem {
  month: string;
  count: number;
  potentialRevenue: number;
}

export default function ForecastPage() {
  const { data, isLoading } = useQuery<{ forecast: ForecastItem[] }>({
    queryKey: ["/api/admin/recurring-payments/forecast"],
  });

  const forecast = data?.forecast || [];
  const totalRevenue = forecast.reduce((s, f) => s + f.potentialRevenue, 0);
  const totalCount = forecast.reduce((s, f) => s + f.count, 0);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Прогноз выручки</h1>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-6 animate-pulse"
            >
              <div className="h-4 bg-muted rounded w-24 mb-3" />
              <div className="h-8 bg-muted rounded w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Прогноз выручки</h1>
      <p className="text-sm text-muted-foreground">
        Прогнозируемая выручка от активных автоплатежей на ближайшие 3 месяца.
      </p>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Потенциальная выручка
            </span>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(totalRevenue)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            За 3 месяца суммарно
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Ожидаемых платежей
            </span>
          </div>
          <div className="text-2xl font-bold">{formatNumber(totalCount)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Средний платёж
            </span>
          </div>
          <div className="text-2xl font-bold">
            {totalCount > 0
              ? formatCurrency(totalRevenue / totalCount)
              : formatCurrency(0)}
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {forecast.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          Нет активных автоплатежей с запланированными датами.
        </div>
      ) : (
        <div className="space-y-4">
          {forecast.map((f, i) => {
            const monthLabel = new Date(f.month).toLocaleDateString("ru-RU", {
              month: "long",
              year: "numeric",
            });
            const maxRevenue = Math.max(...forecast.map((x) => x.potentialRevenue));
            const barWidth =
              maxRevenue > 0 ? (f.potentialRevenue / maxRevenue) * 100 : 0;

            return (
              <div
                key={i}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium capitalize">{monthLabel}</span>
                  <div className="text-right">
                    <div className="font-bold">
                      {formatCurrency(f.potentialRevenue)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatNumber(f.count)} платежей
                    </div>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2 transition-all"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
